import { useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";
import {
  SiGithub,
  SiLinkedin,
  SiX,
  SiMedium,
  SiHashnode,
} from "react-icons/si";
import { personal } from "../data/personal";

gsap.registerPlugin(ScrollTrigger);

const SOCIALS = [
  { href: personal.socials.x, Icon: SiX, label: "X", handle: "@RVishvjeet_" },
  {
    href: personal.socials.github,
    Icon: SiGithub,
    label: "GitHub",
    handle: "Vishvjeet-Rana",
  },
  {
    href: personal.socials.linkedin,
    Icon: SiLinkedin,
    label: "LinkedIn",
    handle: "vishvjeet-rana",
  },
  {
    href: personal.socials.medium,
    Icon: SiMedium,
    label: "Medium",
    handle: "vishvjeetrana",
  },
  {
    href: personal.socials.hashnode,
    Icon: SiHashnode,
    label: "Hashnode",
    handle: "vishvjeetrana",
  },
];

const OPEN_FOR = [
  "Product & full-stack opportunities",
  "Freelance projects",
  "AI-accelerated builds",
  "Meaningful collaborations",
];

const Signal = () => {
  const sectionRef = useRef(null);

  useGSAP(
    () => {
      const pref = window.matchMedia(
        "(prefers-reduced-motion: reduce)",
      ).matches;
      if (pref) {
        gsap.set(
          [
            ".sig-hdr",
            ".sig-heading",
            ".sig-open-for",
            ".sig-cta",
            ".sig-social",
            ".sig-img",
            ".sig-footer",
          ],
          {
            opacity: 1,
            x: 0,
            y: 0,
          },
        );
        return;
      }

      gsap.set(".sig-hdr", { opacity: 0, y: -12 });
      gsap.set(".sig-heading", { opacity: 0, x: -40 });
      gsap.set(".sig-open-for", { opacity: 0, y: 16 });
      gsap.set(".sig-cta", { opacity: 0, y: 12 });
      gsap.set(".sig-social", { opacity: 0, y: 10, scale: 0.94 });
      gsap.set(".sig-img", { opacity: 0, x: 50, scale: 0.96 });
      gsap.set(".sig-footer", { opacity: 0 });

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top 62%",
          once: true,
        },
      });

      tl.to(".sig-hdr", { opacity: 1, y: 0, duration: 0.5, ease: "power3.out" })
        .to(
          ".sig-img",
          { opacity: 1, x: 0, scale: 1, duration: 1.1, ease: "expo.out" },
          "-=0.3",
        )
        .to(
          ".sig-heading",
          { opacity: 1, x: 0, duration: 0.8, ease: "expo.out" },
          "-=0.85",
        )
        .to(
          ".sig-open-for",
          { opacity: 1, y: 0, duration: 0.6, ease: "power3.out" },
          "-=0.5",
        )
        .to(
          ".sig-cta",
          { opacity: 1, y: 0, duration: 0.55, ease: "power3.out" },
          "-=0.38",
        )
        .to(
          ".sig-social",
          {
            opacity: 1,
            y: 0,
            scale: 1,
            stagger: 0.07,
            duration: 0.4,
            ease: "back.out(1.4)",
          },
          "-=0.3",
        )
        .to(".sig-footer", { opacity: 1, duration: 0.45 }, "-=0.2");
    },
    { scope: sectionRef },
  );

  return (
    <section
      id="signal"
      ref={sectionRef}
      className="relative z-[30] overflow-hidden bg-white min-h-[100dvh] md:sticky md:top-0 md:h-dvh"
    >
      <div
        className="relative flex h-full flex-col"
        style={{
          padding:
            "clamp(16px,3.2vw,40px) clamp(12px,3.5vw,48px) clamp(16px,2.8vw,36px)",
        }}
      >
        {/* ── Header row ── */}
        <div className="sig-hdr flex flex-shrink-0 items-center">
          <p
            className="font-robert-medium uppercase"
            style={{
              fontSize: "12px",
              letterSpacing: "0.3em",
              color: "#8E8E93",
            }}
          >
            The Signal
          </p>
        </div>

        {/* ── Main: two columns ── */}
        <div
          className="flex flex-col md:flex-row flex-1 min-h-0"
          style={{
            gap: "clamp(20px,4vw,64px)",
            marginTop: "clamp(14px,2vw,28px)",
          }}
        >
          {/* LEFT: Content */}
          <div
            className="flex flex-col justify-start md:justify-center w-full flex-none md:w-[52%]"
            style={{ gap: "clamp(12px,2.4vw,30px)" }}
          >
            {/* Heading */}
            <div className="sig-heading">
              <h2
                className="font-robert-medium font-bold leading-[1.02] text-[#0D0D0D]"
                style={{
                  fontSize: "clamp(2.4rem,4.8vw,5rem)",
                  letterSpacing: "-0.03em",
                }}
              >
                Open to the
                <br />
                right conversations.
              </h2>
              <p
                className="font-robert-regular mt-4 text-[#5f5f61]"
                style={{
                  fontSize: "clamp(0.76rem,0.98vw,0.88rem)",
                  lineHeight: 1.75,
                  maxWidth: "38ch",
                }}
              >
                Early career, not early thinking. I've shipped real products,
                taken full ownership, and I move fast. If you're building
                something that matters — I'd love to be part of it.
              </p>
            </div>

            {/* Open for */}
            <div
              className="sig-open-for"
              style={{ display: "flex", flexDirection: "column", gap: "9px" }}
            >
              <p
                className="font-robert-medium uppercase"
                style={{
                  fontSize: "8px",
                  letterSpacing: "0.3em",
                  color: "#A1A1AA",
                  marginBottom: "2px",
                }}
              >
                Open for
              </p>
              {OPEN_FOR.map((item) => (
                <div
                  key={item}
                  style={{ display: "flex", alignItems: "center", gap: "10px" }}
                >
                  <div
                    style={{
                      width: "5px",
                      height: "5px",
                      borderRadius: "50%",
                      background: "#0A84FF",
                      flexShrink: 0,
                      opacity: 0.7,
                    }}
                  />
                  <p
                    className="font-robert-medium"
                    style={{
                      fontSize: "clamp(0.74rem,0.95vw,0.84rem)",
                      color: "#3A3A3C",
                      letterSpacing: "0.01em",
                    }}
                  >
                    {item}
                  </p>
                </div>
              ))}
            </div>

            {/* Email CTA */}
            <div className="sig-cta">
              <p
                className="font-robert-medium uppercase"
                style={{
                  fontSize: "8px",
                  letterSpacing: "0.3em",
                  color: "#A1A1AA",
                  marginBottom: "7px",
                }}
              >
                Get in touch
              </p>
              <a
                href={`mailto:${personal.email}`}
                className="font-robert-medium font-bold text-[#0D0D0D]"
                style={{
                  fontSize: "clamp(0.88rem,1.3vw,1.22rem)",
                  letterSpacing: "-0.01em",
                  textDecoration: "none",
                  transition: "color 0.2s",
                  display: "inline-block",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.color = "#0A84FF")}
                onMouseLeave={(e) => (e.currentTarget.style.color = "#0D0D0D")}
              >
                {personal.email}
              </a>
            </div>

            {/* Socials */}
            <div>
              <p
                className="font-robert-medium uppercase"
                style={{
                  fontSize: "8px",
                  letterSpacing: "0.3em",
                  color: "#A1A1AA",
                  marginBottom: "10px",
                }}
              >
                Find me on
              </p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "7px" }}>
                {SOCIALS.map(({ href, Icon, label, handle }) => (
                  <a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    className="sig-social"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "7px",
                      padding: "7px 13px",
                      borderRadius: "9999px",
                      background: "rgba(28,28,30,0.04)",
                      border: "1px solid rgba(28,28,30,0.08)",
                      textDecoration: "none",
                      transition:
                        "background 0.2s ease, border-color 0.2s ease, transform 0.2s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "rgba(28,28,30,0.09)";
                      e.currentTarget.style.borderColor = "rgba(28,28,30,0.18)";
                      e.currentTarget.style.transform = "translateY(-2px)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "rgba(28,28,30,0.04)";
                      e.currentTarget.style.borderColor = "rgba(28,28,30,0.08)";
                      e.currentTarget.style.transform = "translateY(0)";
                    }}
                  >
                    <Icon size={12} color="#3A3A3C" />
                    <span
                      className="font-robert-medium"
                      style={{
                        fontSize: "clamp(0.6rem,0.76vw,0.68rem)",
                        color: "#1C1C1E",
                        letterSpacing: "0.03em",
                      }}
                    >
                      {label}
                    </span>
                    <span
                      className="font-robert-medium"
                      style={{
                        fontSize: "clamp(0.55rem,0.7vw,0.63rem)",
                        color: "#A1A1AA",
                        letterSpacing: "0.02em",
                      }}
                    >
                      {handle}
                    </span>
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* RIGHT: Image */}
          <div className="sig-img relative flex flex-none w-full h-[45vh] items-end justify-center md:flex-1 md:h-auto md:w-auto">
            {/* Subtle blue ground glow */}
            <div
              aria-hidden="true"
              style={{
                position: "absolute",
                bottom: 0,
                left: "10%",
                right: "10%",
                height: "35%",
                background:
                  "radial-gradient(ellipse, rgba(10,132,255,0.07) 0%, transparent 70%)",
                filter: "blur(36px)",
                pointerEvents: "none",
              }}
            />
            <img
              src="/img/enthusiasm.png"
              alt="Vishvjeet Rana — enthusiastic"
              draggable={false}
              className="h-full w-auto md:h-[90dvh]"
              style={{
                position: "relative",
                zIndex: 1,
                mixBlendMode: "multiply",
                animation: "gentle-float 5.2s ease-in-out infinite",
                filter: "drop-shadow(0 12px 36px rgba(10,132,255,0.09))",
                userSelect: "none",
              }}
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default Signal;
